import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";

import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import {
  ACTIVITIES_AVAILABILITY_PATH,
  ACTIVITY_URL_PARAM_KEYS,
} from "@overrides/utilities";
import dayjs from "dayjs";
import {
  ActivitiesAvailabilityActions,
  ActivitiesAvailabilitySelectors,
} from "../../store";
import type { ActivityLocationSuggestion } from "../ActivityLocationAutocomplete";

interface FormDateRange {
  from?: string;
  until?: string;
}

export const useActivityAvailabilitySearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { t, formatDateTime } = useI18nContext();

  const dateRange = useSelector(ActivitiesAvailabilitySelectors.getDateRange);
  const travelers = useSelector(ActivitiesAvailabilitySelectors.getTravelers);
  const place = useSelector(ActivitiesAvailabilitySelectors.getPlace);

  const [formDateRange, setFormDateRange] = useState<FormDateRange>({});

  // Initial setup
  useEffect(() => {
    if (
      formDateRange.from == null &&
      formDateRange.until == null &&
      dateRange != null
    ) {
      setFormDateRange({ from: dateRange.from, until: dateRange.until });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const handleDateRangeFromChange = (date: string | undefined) => {
    setFormDateRange((prev) => ({ ...prev, from: date, until: undefined }));
  };

  const handleDateRangeUntilChange = (date: string | undefined) => {
    setFormDateRange((prev) => ({ ...prev, until: date }));
  };

  const handlePlaceChange = (
    newPlace: ActivityLocationSuggestion | null,
    activeSearch = false
  ) => {
    if (newPlace != null) {
      dispatch(ActivitiesAvailabilityActions.setNewPlace(newPlace));
      if (activeSearch) {
        handleSearchClick();
      }
    }
  };

  const handleTravelersChange = (count: number) => {
    dispatch(ActivitiesAvailabilityActions.setNewTravelers(count));
  };

  const isFormValid = useMemo(() => {
    return (
      formDateRange?.from != null &&
      place != null &&
      place.label != null &&
      place.label.trim() !== ""
    );
  }, [formDateRange, place]);

  const getAvailabilityPath = useCallback(() => {
    if (isFormValid && place != null && formDateRange?.from != null) {
      const destinationCode = encodeURIComponent(
        place.label + (place.subLabel ? `, ${place.subLabel}` : "")
      );

      const untilDate = formDateRange?.until ?? formDateRange.from;

      const searchParams = new URLSearchParams({
        [ACTIVITY_URL_PARAM_KEYS.FROM_DATE]: dayjs(formDateRange.from).format(
          "YYYY-MM-DD"
        ),
        [ACTIVITY_URL_PARAM_KEYS.UNTIL_DATE]:
          dayjs(untilDate).format("YYYY-MM-DD"),
        ...(travelers && {
          [ACTIVITY_URL_PARAM_KEYS.ADULTS_COUNT]: travelers.toString(),
        }),
      });

      return `${ACTIVITIES_AVAILABILITY_PATH}/${destinationCode}?${searchParams.toString()}`;
    }
    return null;
  }, [formDateRange, travelers, isFormValid, place]);

  const handleSearchClick = useCallback(() => {
    const path = getAvailabilityPath();
    if (path != null) {
      navigate(path);
    }
  }, [getAvailabilityPath, navigate]);

  const handleDateRangeClose = useCallback(
    (activeSearch = false) => {
      const { from, until } = formDateRange;
      if (from != null) {
        dispatch(ActivitiesAvailabilityActions.setNewDateFrom(from));
        if (until != null) {
          dispatch(ActivitiesAvailabilityActions.setNewDateUntil(until));
        }
        if (activeSearch) {
          handleSearchClick();
        }
      }
    },
    [formDateRange, dispatch, handleSearchClick]
  );

  const formatDateSummary = useCallback(() => {
    if (formDateRange?.from == null) return "";

    const fromFormatted = formatDateTime(
      dayjs(formDateRange.from).toDate(),
      DateTimeFormatStyle.ShortMonthDayShortWeekday
    );

    if (formDateRange?.until != null) {
      const untilFormatted = formatDateTime(
        dayjs(formDateRange.until).toDate(),
        DateTimeFormatStyle.ShortMonthDayShortWeekday
      );
      return `${fromFormatted} - ${untilFormatted}`;
    }

    return fromFormatted;
  }, [formDateRange, formatDateTime]);

  return {
    context: {
      dateRange: formDateRange,
      travelers,
      place,
    },
    handlers: {
      handleDateRangeFromChange,
      handleDateRangeUntilChange,
      handlePlaceChange,
      handleTravelersChange,
      handleDateRangeClose,
      handleSearchClick,
    },
    text: {
      locationLabel: t("core-activities.search.locationLabel"),
      locationData: place?.label,
      searchSummary: formatDateSummary(),
      search: t("core-activities.search.searchButtonLabel"),
    },
    isFormValid,
  };
};
